import Seo from "@/components/Seo";
import { useState, useEffect } from "react";
import { useNavigate, Routes, Route } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import axios from "axios";
import { 
  Home, CreditCard, Clock, LogOut, Menu, X, ChevronRight, 
  Check, MapPin, Ship, Waves, Fish, Wine, Download, Smartphone,
  CalendarDays, QrCode, Sparkles, AlertCircle, User, Globe, Ticket, Lock
} from "lucide-react";

import { PassAlgarveLogo, PassAlgarveLogoCompact } from "@/components/PassAlgarveLogo";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import BookingSection from "@/components/BookingSection";
import { loc } from "@/lib/locale";

const ATTRACTION_ICONS = {
  "ponta-piedade": Ship,
  "benagil-cave": Waves,
  "dolphin-watching": Fish,
  "bon-vivant": Wine
};

// Legacy fallback (only used if the passport's type no longer exists in DB)
const LEGACY_ATTRACTIONS = [
  { id: "ponta-piedade", name: "Ponta da Piedade" },
  { id: "benagil-cave", name: "Gruta de Benagil" },
  { id: "dolphin-watching", name: "Observacao dos Golfinhos" },
  { id: "bon-vivant", name: "Happy Hour Bon Vivant" }
];

const iconForAttraction = (a) => {
  if (ATTRACTION_ICONS[a.id]) return ATTRACTION_ICONS[a.id];
  const n = (a.name || "").toLowerCase();
  if (n.includes("golfinho") || n.includes("dolphin")) return Fish;
  if (n.includes("gruta") || n.includes("cave") || n.includes("benagil")) return Waves;
  if (n.includes("barco") || n.includes("boat") || n.includes("veleiro") || n.includes("cruzeiro")) return Ship;
  if (n.includes("happy hour") || n.includes("bar ") || n.includes("wine") || n.includes("cocktail")) return Wine;
  return MapPin;
};

// Passport Card Component
const PassportCard = ({ passport, onFlip, isFlipped, t }) => {
  const expiryDate = new Date(passport.expiry_date);
  const isExpired = expiryDate < new Date();
  const visitedCount = passport.visits?.length || 0;

  return (
    <div 
      className={`passport-card w-full max-w-md h-64 cursor-pointer ${isFlipped ? 'flipped' : ''}`}
      onClick={onFlip}
    >
      <div className="passport-inner">
        {/* Front */}
        <div className="passport-front bg-gradient-to-br from-navy to-navy-dark rounded-2xl p-6 border border-gold/30 shadow-xl">
          <div className="flex justify-between items-start mb-4">
            <PassAlgarveLogo size="small" />
            <Badge className={isExpired ? "bg-red-500/20 text-red-400" : "bg-emerald-500/20 text-emerald-400"}>
              {isExpired ? t('dashboard.expired') : t('dashboard.active')}
            </Badge>
          </div>
          
          <div className="mt-8">
            <p className="text-white/60 text-xs uppercase tracking-widest font-body">{t('dashboard.holder')}</p>
            <p className="text-white font-heading text-xl">{passport.user_name}</p>
            {passport.passport_type_name && (
              <p className="text-gold font-accent text-sm italic mt-1">{passport.passport_type_name}</p>
            )}
          </div>
          
          <div className="flex justify-between mt-4">
            <div>
              <p className="text-white/60 text-xs uppercase tracking-widest font-body">{t('dashboard.validity')}</p>
              <p className="text-white font-body">{expiryDate.toLocaleDateString('pt-PT')}</p>
            </div>
            <div>
              <p className="text-white/60 text-xs uppercase tracking-widest font-body">{t('dashboard.visits')}</p>
              <p className="text-white font-body">{visitedCount}/4</p>
            </div>
          </div>
          
          <p className="text-gold text-xs text-center mt-4 font-body">{t('dashboard.tapForQR')}</p>
        </div>

        {/* Back */}
        <div className="passport-back bg-white rounded-2xl p-6 flex flex-col items-center justify-center shadow-xl">
          <QRCodeSVG 
            value={passport.qr_code} 
            size={140}
            level="H"
            includeMargin
            className="mb-4"
          />
          <p className="font-mono text-navy font-bold text-lg">{passport.qr_code}</p>
          <p className="text-navy/60 text-xs mt-2 font-body">{t('dashboard.showAtAttraction')}</p>
        </div>
      </div>
    </div>
  );
};

// Dashboard Home
const DashboardHome = () => {
  const { user, token, API } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [passports, setPassports] = useState([]);
  const [passportTypes, setPassportTypes] = useState([]);
  const [attractionsMap, setAttractionsMap] = useState({});
  const [bookingByType, setBookingByType] = useState({});
  const [includedByType, setIncludedByType] = useState({});
  const [loading, setLoading] = useState(true);
  const [flippedPassport, setFlippedPassport] = useState(null);
  const [buyingId, setBuyingId] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [pRes, tRes, aRes] = await Promise.all([
          axios.get(`${API}/passports/my`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/passport-types`),
          axios.get(`${API}/attractions`),
        ]);
        setPassports(pRes.data || []);
        setPassportTypes(tRes.data || []);
        const map = {};
        (aRes.data || []).forEach(a => { map[a.id] = a; });
        setAttractionsMap(map);

        // Attractions actually included in each purchased passport type (works for legacy/inactive types)
        const typeIds = [...new Set((pRes.data || []).map(p => p.passport_type_id).filter(Boolean))];
        const entries = await Promise.all(typeIds.map(async (tid) => {
          try {
            const iRes = await axios.get(`${API}/passport-types/${tid}/included-attractions`);
            return [tid, iRes.data || []];
          } catch { return [tid, []]; }
        }));
        const includedMap = Object.fromEntries(entries);
        setIncludedByType(includedMap);
        setBookingByType(Object.fromEntries(
          Object.entries(includedMap).map(([tid, list]) => [tid, list.filter(a => a.requires_booking)])
        ));
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
        toast.error(t('dashboard.loadError'));
      } finally {
        setLoading(false);
      }
    })();
  }, [API, token, t]);

  const handleBuyPass = async (passportTypeId) => {
    if (!passportTypeId) {
      toast.error(t('dashboard.checkoutError'));
      return;
    }
    setBuyingId(passportTypeId);
    try {
      const response = await axios.post(
        `${API}/checkout/create-session`,
        { origin_url: window.location.origin, passport_type_id: passportTypeId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.location.href = response.data.url;
    } catch (error) {
      console.error("Checkout error:", error);
      toast.error(t('dashboard.checkoutError'));
      setBuyingId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gold"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <Seo noindex title={language === "en" ? "My Account" : "Minha Conta"} />
      <div>
        <h1 className="font-heading text-3xl text-navy mb-2">{t('dashboard.hello')}, {user?.name}!</h1>
        <p className="text-navy/60 font-body">{t('dashboard.welcomePanel')}</p>
      </div>

      {passports.length === 0 ? (
        <div className="space-y-6">
          <div className="text-center max-w-2xl mx-auto">
            <div className="w-20 h-20 bg-gold/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <CreditCard className="w-10 h-10 text-gold" />
            </div>
            <h3 className="font-heading text-2xl text-navy mb-2">{t('dashboard.noPassportTitle')}</h3>
            <p className="text-navy/60 font-body">{t('dashboard.noPassportSubtitle')}</p>
          </div>

          {passportTypes.length === 0 ? (
            <Card className="bg-white border-gold/20 shadow-lg">
              <CardContent className="py-8 text-center text-navy/60">
                {t('offer.noPassports')}
              </CardContent>
            </Card>
          ) : (
            <div className={`grid grid-cols-1 ${
              passportTypes.length === 1 ? 'md:grid-cols-1 max-w-md mx-auto' :
              passportTypes.length === 2 ? 'md:grid-cols-2 max-w-4xl mx-auto' :
              'md:grid-cols-2 lg:grid-cols-3'
            } gap-6`}>
              {passportTypes.map((pt) => {
                const isBuying = buyingId === pt.id;
                const hasDiscount = pt.discount_amount > 0 && pt.sum_total > 0;
                return (
                  <Card
                    key={pt.id}
                    className="bg-white border-gold/20 shadow-lg hover:shadow-xl transition-shadow flex flex-col"
                    data-testid={`dashboard-passport-card-${pt.id}`}
                  >
                    <CardContent className="pt-6 flex-1 flex flex-col">
                      <div className="flex items-start justify-between mb-3">
                        <h4 className="font-heading text-xl text-navy">{loc(pt, "name", language)}</h4>
                        {hasDiscount && pt.discount_percent >= 20 && (
                          <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">
                            -{Math.round(pt.discount_percent)}%
                          </Badge>
                        )}
                      </div>

                      <p className="text-navy/70 text-sm mb-4 font-body line-clamp-2">{pt.description}</p>

                      <div className="flex items-baseline gap-2 mb-2">
                        <span className="font-heading text-3xl font-bold text-gold">€{Number(pt.price).toFixed(0)}</span>
                        <span className="text-navy/50 text-sm">/{t('passports.perPerson').replace('/', '')}</span>
                      </div>
                      {hasDiscount && (
                        <div className="flex items-center gap-2 mb-3">
                          <span className="text-navy/40 text-xs line-through">€{pt.sum_total.toFixed(2)}</span>
                          <span className="text-emerald-700 text-xs font-semibold">
                            {t('passports.youSave') || 'Poupe'} €{pt.discount_amount.toFixed(2)}
                          </span>
                        </div>
                      )}

                      <div className="space-y-1.5 mb-4 text-sm text-navy/70">
                        <div className="flex items-center gap-2">
                          <Check className="w-4 h-4 text-gold flex-shrink-0" />
                          <span>{t('passports.validity')} {pt.validity_days} {t('passports.days')}</span>
                        </div>
                        {(pt.attractions || []).map((aid) => (
                          <div key={aid} className="flex items-center gap-2">
                            <MapPin className="w-4 h-4 text-gold flex-shrink-0" />
                            <span className="truncate">{loc(attractionsMap[aid], "name", language) || aid}</span>
                          </div>
                        ))}
                      </div>

                      <Button
                        onClick={() => handleBuyPass(pt.id)}
                        disabled={isBuying || buyingId !== null}
                        className="bg-gold hover:bg-gold-dark text-white rounded-full mt-auto disabled:opacity-50"
                        data-testid={`buy-passport-btn-${pt.id}`}
                      >
                        {isBuying ? "A processar..." : `${t('dashboard.buyPassportBtn')} – €${Number(pt.price).toFixed(0)}`}
                        {!isBuying && <ChevronRight className="ml-2 w-4 h-4" />}
                      </Button>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-6">
          <h2 id="meus-passaportes" className="font-heading text-xl text-navy scroll-mt-28">{t('dashboard.myPassports')}</h2>

          {passports.map((passport) => {
            const expiryDate = new Date(passport.expiry_date);
            const isExpired = expiryDate < new Date();
            const visits = passport.visits || [];
            const usedIds = new Set(visits.map(v => v.attraction_id));
            const includedAttractions = (includedByType[passport.passport_type_id]?.length
              ? includedByType[passport.passport_type_id]
              : LEGACY_ATTRACTIONS);
            const totalAttractions = includedAttractions.length;
            const usedCount = visits.length;
            const remainingCount = Math.max(0, totalAttractions - usedCount);
            const daysLeft = Math.max(0, Math.ceil((expiryDate - new Date()) / (1000 * 60 * 60 * 24)));
            const progressPct = Math.round((usedCount / totalAttractions) * 100);
            const bookingAttractions = bookingByType[passport.passport_type_id] || [];
            const typeDef = passportTypes.find(p => p.id === passport.passport_type_id);
            const typeName = (language === "en" && typeDef?.name_en) || passport.passport_type_name;

            return (
              <div key={passport.id} className="space-y-6">
                {/* Side-by-side: Card + QR */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Passport Card (no flip) */}
                  <div
                    className="rounded-2xl p-6 border border-gold/30 shadow-xl bg-gradient-to-br from-navy to-navy-dark relative overflow-hidden"
                    data-testid={`passport-card-${passport.id}`}
                  >
                    {/* Decorative gold rings */}
                    <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-gold/5 blur-2xl pointer-events-none" />
                    <div className="absolute -bottom-20 -left-10 w-48 h-48 rounded-full bg-gold/5 blur-2xl pointer-events-none" />

                    <div className="relative">
                      <div className="flex justify-between items-start mb-6">
                        <PassAlgarveLogo size="small" />
                        <Badge className={isExpired ? "bg-red-500/20 text-red-400 border border-red-500/30" : "bg-emerald-500/20 text-emerald-400 border border-emerald-500/30"}>
                          {isExpired ? t('dashboard.expired') : t('dashboard.active')}
                        </Badge>
                      </div>

                      <div>
                        <p className="text-white/60 text-xs uppercase tracking-widest font-body">{t('dashboard.holder')}</p>
                        <p className="text-white font-heading text-2xl mt-0.5" data-testid={`passport-holder-${passport.id}`}>
                          {(passport.holder_name || "").trim() || passport.user_name}
                        </p>
                        {typeName && (
                          <p className="text-gold font-accent text-sm italic mt-1">{typeName}</p>
                        )}
                      </div>

                      {(passport.holder_name || "").trim() && passport.holder_name.trim() !== passport.user_name && (
                        <p className="mt-3 text-white/80 text-xs" data-testid={`holder-name-${passport.id}`}>
                          <span className="text-white/50 text-[10px] uppercase tracking-widest">{language === 'en' ? 'Account' : 'Conta'}:</span>{" "}
                          <span className="font-semibold">{passport.user_name}</span>
                        </p>
                      )}

                      <div className="grid grid-cols-3 gap-3 mt-6">
                        <div>
                          <p className="text-white/50 text-[10px] uppercase tracking-widest font-body">{t('dashboard.validity')}</p>
                          <p className="text-white font-body text-sm mt-0.5">{expiryDate.toLocaleDateString('pt-PT')}</p>
                          <p className="text-gold/80 text-[10px] mt-0.5">{daysLeft} {daysLeft === 1 ? t('dash2.day') : t('dash2.days')}</p>
                        </div>
                        <div>
                          <p className="text-white/50 text-[10px] uppercase tracking-widest font-body">{t('dashboard.visits')}</p>
                          <p className="text-white font-body text-sm mt-0.5">{usedCount}/{totalAttractions}</p>
                          <p className="text-gold/80 text-[10px] mt-0.5">{progressPct}% {t('dash2.used')}</p>
                        </div>
                        <div>
                          <p className="text-white/50 text-[10px] uppercase tracking-widest font-body">{t('dash2.code')}</p>
                          <p className="text-white font-mono text-xs mt-0.5 truncate">{passport.qr_code}</p>
                        </div>
                      </div>

                      {/* Progress bar */}
                      <div className="mt-5">
                        <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-gradient-to-r from-gold to-amber-300 transition-all duration-700"
                            style={{ width: `${progressPct}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* QR card (always visible) */}
                  <div
                    className="bg-white rounded-2xl p-6 flex flex-col items-center justify-center shadow-xl border border-gold/20"
                    data-testid={`passport-qr-${passport.id}`}
                  >
                    <div className="flex items-center gap-2 text-navy/60 text-xs font-body uppercase tracking-widest mb-3">
                      <QrCode className="w-3.5 h-3.5 text-gold" /> {t('dashboard.showAtAttraction')}
                    </div>
                    <div className="p-3 bg-white rounded-xl border border-gold/10">
                      <QRCodeSVG
                        value={passport.qr_code}
                        size={180}
                        level="H"
                        includeMargin={false}
                      />
                    </div>
                    <p className="font-mono text-navy font-bold text-lg mt-3 tracking-wider">{passport.qr_code}</p>
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-3 text-xs"
                      onClick={() => {
                        navigator.clipboard.writeText(passport.qr_code);
                        toast.success(t('dash2.codeCopied'));
                      }}
                      data-testid={`copy-qr-${passport.id}`}
                    >
                      {t('dash2.copyCode')}
                    </Button>
                  </div>
                </div>

                {/* Booking-required experiences */}
                <BookingSection attractions={bookingAttractions} />

                {/* KPI strip */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="bg-white rounded-xl p-4 border border-gold/15">
                    <div className="flex items-center gap-2 text-navy/60 text-xs uppercase tracking-widest">
                      <Sparkles className="w-3.5 h-3.5 text-gold" /> {t('dash2.available')}
                    </div>
                    <p className="font-heading text-3xl text-navy mt-1">{remainingCount}</p>
                    <p className="text-navy/40 text-xs">{t('dash2.toUse')}</p>
                  </div>
                  <div className="bg-white rounded-xl p-4 border border-gold/15">
                    <div className="flex items-center gap-2 text-navy/60 text-xs uppercase tracking-widest">
                      <Check className="w-3.5 h-3.5 text-emerald-500" /> {t('dash2.alreadyUsed')}
                    </div>
                    <p className="font-heading text-3xl text-navy mt-1">{usedCount}</p>
                    <p className="text-navy/40 text-xs">{t('dash2.of')} {totalAttractions}</p>
                  </div>
                  <div className="bg-white rounded-xl p-4 border border-gold/15">
                    <div className="flex items-center gap-2 text-navy/60 text-xs uppercase tracking-widest">
                      <CalendarDays className="w-3.5 h-3.5 text-blue-500" /> {t('dash2.validity')}
                    </div>
                    <p className="font-heading text-3xl text-navy mt-1">{daysLeft}d</p>
                    <p className="text-navy/40 text-xs">{isExpired ? t('dash2.expiredLower') : t('dash2.remaining')}</p>
                  </div>
                  <div className="bg-white rounded-xl p-4 border border-gold/15">
                    <div className="flex items-center gap-2 text-navy/60 text-xs uppercase tracking-widest">
                      <CreditCard className="w-3.5 h-3.5 text-purple-500" /> {t('dash2.status')}
                    </div>
                    <p className={`font-heading text-xl mt-1 ${isExpired ? "text-red-600" : "text-emerald-600"}`}>
                      {isExpired ? t('dashboard.expired') : t('dashboard.active')}
                    </p>
                    <p className="text-navy/40 text-xs">{typeName?.split(" ").slice(0, 2).join(" ") || t('dash2.passportFallback')}</p>
                  </div>
                </div>

                {/* Available attractions */}
                <Card className="bg-white border-gold/20 shadow-lg">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="font-heading text-lg text-navy flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-gold" />
                        {t('dash2.availableAttractions')}
                      </CardTitle>
                      <span className="text-xs text-navy/40">{remainingCount} {t('dash2.remaining')}</span>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {remainingCount === 0 ? (
                      <div className="py-8 text-center text-navy/50 text-sm">
                        {t('dash2.allVisited')}
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {includedAttractions.filter(a => !usedIds.has(a.id)).map((attraction) => {
                          const Icon = iconForAttraction(attraction);
                          return (
                            <div
                              key={attraction.id}
                              className="p-4 rounded-xl border bg-gold/5 border-gold/20 hover:bg-gold/10 transition-colors"
                              data-testid={`attraction-available-${attraction.id}`}
                            >
                              <div className="flex items-center gap-3">
                                <div className="w-11 h-11 rounded-full bg-gold/20 text-gold flex items-center justify-center flex-shrink-0">
                                  <Icon className="w-5 h-5" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="font-body text-navy font-semibold text-sm">{loc(attraction, "name", language)}</p>
                                  {attractionsMap[attraction.id]?.requires_booking && (
                                    <span className="inline-flex items-center gap-1 bg-navy text-gold text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full mt-1" data-testid={`booking-tag-${attraction.id}`}>
                                      <CalendarDays className="w-2.5 h-2.5" /> {language === 'en' ? 'Booking required' : 'Agendamento obrigatório'}
                                    </span>
                                  )}
                                  <p className="text-navy/50 text-xs flex items-center gap-1 mt-0.5">
                                    <AlertCircle className="w-3 h-3" /> {t('dash2.showQr')}
                                  </p>
                                </div>
                                <Badge className="bg-gold/15 text-gold border-0 text-[10px]">{t('dash2.availableBadge')}</Badge>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Used attractions */}
                <Card className="bg-white border-gold/20 shadow-lg">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="font-heading text-lg text-navy flex items-center gap-2">
                        <Check className="w-4 h-4 text-emerald-500" />
                        {t('dash2.usedAttractions')}
                      </CardTitle>
                      <span className="text-xs text-navy/40">{usedCount} {t('dash2.of')} {totalAttractions}</span>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {usedCount === 0 ? (
                      <div className="py-8 text-center text-navy/50 text-sm">
                        {t('dash2.noneUsedYet')}
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {includedAttractions.filter(a => usedIds.has(a.id)).map((attraction) => {
                          const visit = visits.find(v => v.attraction_id === attraction.id);
                          const Icon = iconForAttraction(attraction);
                          return (
                            <div
                              key={attraction.id}
                              className="p-4 rounded-xl border bg-emerald-50 border-emerald-200"
                              data-testid={`attraction-used-${attraction.id}`}
                            >
                              <div className="flex items-center gap-3">
                                <div className="w-11 h-11 rounded-full bg-emerald-500 text-white flex items-center justify-center flex-shrink-0">
                                  <Check className="w-5 h-5" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="font-body text-emerald-800 font-semibold text-sm">{loc(attraction, "name", language)}</p>
                                  {visit && (
                                    <p className="text-emerald-600/80 text-xs mt-0.5">
                                      {t('dash2.visitedOn')} {new Date(visit.validated_at).toLocaleDateString('pt-PT')}
                                    </p>
                                  )}
                                </div>
                                <div className="w-9 h-9 rounded-full bg-white text-emerald-600 flex items-center justify-center">
                                  <Icon className="w-4 h-4" />
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// My Data (profile) page
const MyData = () => {
  const { user, token, API, refreshUser } = useAuth();
  const { language } = useLanguage();
  const pt = language !== "en";
  const [name, setName] = useState(user?.name || "");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [savingName, setSavingName] = useState(false);
  const [savingPass, setSavingPass] = useState(false);

  useEffect(() => { setName(user?.name || ""); }, [user]);

  const saveName = async (e) => {
    e.preventDefault();
    setSavingName(true);
    try {
      await axios.put(`${API}/me/profile`, { name }, { headers: { Authorization: `Bearer ${token}` } });
      await refreshUser();
      toast.success(pt ? "Nome atualizado com sucesso" : "Name updated successfully");
    } catch (err) {
      toast.error(err.response?.data?.detail || (pt ? "Erro ao atualizar" : "Update failed"));
    } finally {
      setSavingName(false);
    }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error(pt ? "As senhas não coincidem" : "Passwords do not match");
      return;
    }
    setSavingPass(true);
    try {
      await axios.put(`${API}/me/profile`, { current_password: currentPassword, new_password: newPassword }, { headers: { Authorization: `Bearer ${token}` } });
      setCurrentPassword(""); setNewPassword(""); setConfirmPassword("");
      toast.success(pt ? "Senha alterada com sucesso" : "Password changed successfully");
    } catch (err) {
      toast.error(err.response?.data?.detail || (pt ? "Erro ao alterar senha" : "Password change failed"));
    } finally {
      setSavingPass(false);
    }
  };

  const inputCls = "w-full rounded-md border border-gold/30 focus:border-gold focus:outline-none px-3 py-2.5 text-sm bg-white";

  return (
    <div className="space-y-6" data-testid="my-data-page">
      <div>
        <h1 className="font-heading text-3xl text-navy mb-2">{pt ? "Meus Dados" : "My Data"}</h1>
        <p className="text-navy/60 font-body">{pt ? "Consulte e atualize as informações da sua conta" : "View and update your account information"}</p>
      </div>

      <Card className="bg-white border-gold/20 shadow-lg">
        <CardHeader className="pb-3">
          <CardTitle className="font-heading text-lg text-navy flex items-center gap-2">
            <User className="w-4 h-4 text-gold" /> {pt ? "Informações pessoais" : "Personal information"}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={saveName} className="space-y-4 max-w-md">
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-navy/70 uppercase tracking-wider">{pt ? "Nome" : "Name"}</label>
              <input value={name} onChange={(e) => setName(e.target.value)} required className={inputCls} data-testid="mydata-name-input" />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-navy/70 uppercase tracking-wider">Email</label>
              <input value={user?.email || ""} disabled className={`${inputCls} bg-navy/5 text-navy/60 cursor-not-allowed`} data-testid="mydata-email-input" />
              <p className="text-[11px] text-navy/50">{pt ? "O email não pode ser alterado." : "Email cannot be changed."}</p>
            </div>
            <Button type="submit" disabled={savingName} className="bg-gold hover:bg-gold-dark text-white" data-testid="mydata-save-name-btn">
              {savingName ? (pt ? "A guardar..." : "Saving...") : (pt ? "Guardar alterações" : "Save changes")}
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card className="bg-white border-gold/20 shadow-lg">
        <CardHeader className="pb-3">
          <CardTitle className="font-heading text-lg text-navy flex items-center gap-2">
            <Lock className="w-4 h-4 text-gold" /> {pt ? "Alterar senha" : "Change password"}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={savePassword} className="space-y-4 max-w-md">
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-navy/70 uppercase tracking-wider">{pt ? "Senha atual" : "Current password"}</label>
              <input type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} required className={inputCls} data-testid="mydata-current-password" />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-navy/70 uppercase tracking-wider">{pt ? "Nova senha" : "New password"}</label>
              <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required minLength={6} className={inputCls} data-testid="mydata-new-password" />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-navy/70 uppercase tracking-wider">{pt ? "Confirmar nova senha" : "Confirm new password"}</label>
              <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required minLength={6} className={inputCls} data-testid="mydata-confirm-password" />
            </div>
            <Button type="submit" disabled={savingPass} className="bg-navy hover:bg-navy/90 text-gold" data-testid="mydata-save-password-btn">
              {savingPass ? (pt ? "A alterar..." : "Changing...") : (pt ? "Alterar senha" : "Change password")}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

// Main Customer Dashboard
export default function CustomerDashboard() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { t, language } = useLanguage();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const pt = language !== "en";

  const handleLogout = () => {
    logout();
    navigate("/");
    toast.success(t('dashboard.logoutSuccess'));
  };

  const goToPassports = () => {
    navigate("/dashboard");
    setTimeout(() => {
      document.getElementById("meus-passaportes")?.scrollIntoView({ behavior: "smooth" });
    }, 250);
  };

  const navItems = [
    { icon: Home, label: t('dashboard.home'), onClick: () => navigate("/dashboard"), testid: "nav-inicio" },
    { icon: Ticket, label: pt ? "Meus Passaportes" : "My Passports", onClick: goToPassports, testid: "nav-passaportes" },
    { icon: User, label: pt ? "Meus Dados" : "My Data", onClick: () => navigate("/dashboard/dados"), testid: "nav-dados" },
    { icon: Globe, label: pt ? "Acessar Site" : "Visit Website", onClick: () => navigate("/"), testid: "nav-site" },
  ];

  return (
    <div className="min-h-screen bg-sand">
      {/* Sidebar */}
      <aside className={`fixed top-0 left-0 z-40 h-screen w-64 bg-navy transform transition-transform duration-300 ease-in-out ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0`}>
        <div className="flex flex-col h-full">
          <div className="p-6 border-b border-white/10">
            <PassAlgarveLogo size="small" />
          </div>
          
          <nav className="flex-1 p-4 space-y-2">
            {navItems.map((item) => (
              <button
                key={item.testid}
                onClick={() => {
                  item.onClick();
                  setSidebarOpen(false);
                }}
                className="sidebar-link w-full text-left"
                data-testid={item.testid}
              >
                <item.icon className="w-5 h-5" />
                {item.label}
              </button>
            ))}
          </nav>

          <div className="p-4 border-t border-white/10">
            <div className="flex items-center gap-3 mb-4 px-4">
              <div className="w-10 h-10 rounded-full bg-gold/20 flex items-center justify-center">
                <span className="text-gold font-semibold">{user?.name?.[0]?.toUpperCase()}</span>
              </div>
              <div>
                <p className="text-white font-body text-sm">{user?.name}</p>
                <p className="text-white/50 text-xs">{user?.email}</p>
              </div>
            </div>
            <Button
              variant="ghost"
              onClick={handleLogout}
              className="w-full text-white/70 hover:text-white hover:bg-white/10 justify-start"
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5 mr-3" />
              {t('dashboard.logout')}
            </Button>
            <div className="mt-3 px-3">
              <LanguageSwitcher />
            </div>
          </div>
        </div>
      </aside>

      {/* Mobile header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-30 bg-navy p-4 flex items-center justify-between">
        <PassAlgarveLogoCompact />
        <div className="flex items-center gap-2">
          <LanguageSwitcher variant="navy" />
          <button 
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="text-white p-2"
            data-testid="mobile-sidebar-toggle"
          >
            {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </header>

      {/* Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <main className="lg:ml-64 p-4 lg:p-8 pt-28 lg:pt-8">
        <div className="max-w-4xl mx-auto">
          <Routes>
            <Route index element={<DashboardHome />} />
            <Route path="dados" element={<MyData />} />
          </Routes>
        </div>
      </main>
    </div>
  );
}
